import React, { Suspense, startTransition } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';

interface SafeLazyWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  errorMessage?: string;
}

const LoadingFallback = () => {
  const { t } = useSafeTranslation();

  return (
(
  <div className="flex items-center justify-center min-h-[200px]">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center p-6"
    >
      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-primary-600" />
      <p className="text-neutral-600 font-medium">{t('ui.loading')}</p>
      <p className="text-xs text-neutral-500 mt-1">{t('ui.pleaseWait')}</p>
    </motion.div>
  </div>
)
  );
};

const ErrorFallback: React.FC<{ 
  error: Error; 
  resetErrorBoundary: () => void;
  errorMessage?: string;
}> = ({ error, resetErrorBoundary, errorMessage }) => {
  const [isRetrying, setIsRetrying] = React.useState(false);

  const handleRetry = () => {
    setIsRetrying(true);
    startTransition(() => {
      resetErrorBoundary();
      setTimeout(() => setIsRetrying(false), 1000);
    });
  };

  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center p-6 bg-red-50 rounded-xl border border-red-200 max-w-md"
      >
        <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">
          {errorMessage || 'Something went wrong'}
        </h3>
        <p className="text-red-600 text-sm mb-4">
          {error?.message || 'Failed to load component. Please try again.'}
        </p>
        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 mx-auto"
        >
          <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
          <span>{isRetrying ? 'Retrying...' : 'Try Again'}</span>
        </button>
      </motion.div>
    </div>
  );
};

const SafeLazyWrapper: React.FC<SafeLazyWrapperProps> = ({
  children,
  fallback = <LoadingFallback />,
  errorMessage,
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ErrorFallback {...props} errorMessage={errorMessage} />
      )}
      onError={(error, errorInfo) => {
        console.error('SafeLazyWrapper Error:', error, errorInfo);
        
        // Report to monitoring service
        if (import.meta.env.PROD && typeof window !== 'undefined') {
          try {
            // Send to error tracking service
            if ('gtag' in window) {
              (window as any).gtag('event', 'exception', {
                description: error?.message || 'Component load error',
                fatal: false,
              });
            }
          } catch (reportingError) {
            console.error('Error reporting failed:', reportingError);
          }
        }
      }}
      onReset={() => {
        // Optional: Clear any error state
        console.log('Error boundary reset');
      }}
    >
      <Suspense fallback={fallback}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

export default SafeLazyWrapper;